import { useState, useMemo, useEffect } from 'react';
import { Trophy, TrendingUp, AlertTriangle, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { getLeaderboard } from '@/data/mockData';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import type { LeaderboardScore } from '@/types';
import { StatusDot } from '@/components/StatusBadge';
import { useOnboarding } from '@/hooks/useOnboarding';
import { track } from '@/lib/telemetry';

export default function Leaderboard() {
  const { mode, userId, dealershipId } = useAuth();
  const { deals, activities, customerFeedback, floorReps } = useDealershipData();
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('today');
  const { markStep } = useOnboarding();

  useEffect(() => {
    if (mode === 'real') {
      markStep('viewed_leaderboard_at');
      track('leaderboard_viewed', { userId, dealershipId, mode }, { period });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const scores: LeaderboardScore[] = useMemo(() => {
    if (mode !== 'real') return getLeaderboard(period);
    // Compute live leaderboard from real data
    const now = new Date();
    const cutoff = new Date(now);
    if (period === 'today') cutoff.setHours(0, 0, 0, 0);
    else if (period === 'week') cutoff.setDate(cutoff.getDate() - 7);
    else cutoff.setDate(cutoff.getDate() - 30);
    const cutoffMs = cutoff.getTime();

    return floorReps.map((rep) => {
      const repDeals = deals.filter(d => d.rep_id === rep.id);
      const repActs = activities.filter(a => a.rep_id === rep.id && new Date(a.created_at).getTime() >= cutoffMs);
      const demos = repActs.filter(a => ['walkaround', 'test_drive', 'quote_review', 'followup_video'].includes(a.activity_type)).length;
      const sold = repDeals.filter(d => d.stage === 'sold' && new Date(d.last_activity_at).getTime() >= cutoffMs).length;
      const moved = repDeals.filter(d => new Date(d.last_activity_at).getTime() >= cutoffMs && d.stage !== 'new').length;
      const withNext = repDeals.filter(d => d.stage !== 'sold' && d.next_step).length;
      const totalActive = repDeals.filter(d => d.stage !== 'sold').length;
      const followup_rate = totalActive > 0 ? Math.round((withNext / totalActive) * 100) : 0;
      const repFb = customerFeedback.filter(f => f.rep_id === rep.id);
      const greens = repFb.filter(f => f.rating_color === 'green').length;
      const csi_score = repFb.length > 0 ? Math.round((greens / repFb.length) * 100) : 0;
      const score = Math.min(100, Math.round(demos * 4 + (followup_rate * 0.3) + moved * 4 + sold * 6));
      return {
        id: `live-${rep.id}-${period}`,
        dealership_id: '',
        rep_id: rep.id,
        rep_name: rep.name,
        period_type: period,
        score,
        demos_count: demos,
        followup_rate,
        deals_moved: moved,
        sold_count: sold,
        csi_score,
        updated_at: now.toISOString(),
      } satisfies LeaderboardScore;
    }).sort((a, b) => b.score - a.score);
  }, [mode, period, deals, activities, customerFeedback, floorReps]);

  const topPerformer = scores[0];
  const mostDemos = [...scores].sort((a, b) => b.demos_count - a.demos_count)[0];
  const bestFollowUp = [...scores].sort((a, b) => b.followup_rate - a.followup_rate)[0];
  const atRisk = scores.filter(s => s.score < 75);

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Trophy className="h-6 w-6 text-primary" /> Leaderboard</h1>
        <p className="text-sm text-muted-foreground">Sales performance rankings</p>
      </div>

      <Tabs value={period} onValueChange={v => setPeriod(v as typeof period)}>
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="week">This Week</TabsTrigger>
          <TabsTrigger value="month">This Month</TabsTrigger>
        </TabsList>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2">
            <TabsContent value={period} className="mt-0">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Rank</TableHead>
                        <TableHead>Rep</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead className="text-center">Deals</TableHead>
                        <TableHead className="text-center">Demos</TableHead>
                        <TableHead className="text-center">Follow-Ups</TableHead>
                        <TableHead className="text-center">CSI</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scores.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">
                            No scores yet — once your team logs activity, the board will fill in here.
                          </TableCell>
                        </TableRow>
                      )}
                      {scores.map((s, i) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <span className={`font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-orange-400' : 'text-muted-foreground'}`}>
                              #{i + 1}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{s.rep_name}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold text-lg ${s.score >= 80 ? 'text-green-400' : s.score >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {s.score}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">{s.deals_moved}</TableCell>
                          <TableCell className="text-center">{s.demos_count}</TableCell>
                          <TableCell className="text-center">{s.followup_rate}%</TableCell>
                          <TableCell className="text-center">{s.csi_score}</TableCell>
                          <TableCell>
                            <StatusDot status={s.score >= 80 ? 'active' : s.score >= 70 ? 'slowing' : 'no_activity'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Scoring Breakdown */}
              <Card className="mt-4">
                <CardHeader className="pb-2"><CardTitle className="text-base">Scoring Formula (out of 100)</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Activity', points: 40, color: 'bg-blue-500' },
                      { label: 'Follow-Up', points: 30, color: 'bg-purple-500' },
                      { label: 'Deal Movement', points: 20, color: 'bg-orange-500' },
                      { label: 'Results', points: 10, color: 'bg-green-500' },
                    ].map(item => (
                      <div key={item.label} className="text-center p-3 bg-muted/30 rounded-lg">
                        <div className={`w-3 h-3 rounded-full ${item.color} mx-auto mb-2`} />
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-2xl font-bold">{item.points}</p>
                        <p className="text-xs text-muted-foreground">points</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>

          {/* Right Panel */}
          <div className="space-y-4">
            {topPerformer && (
              <Card className="border-yellow-500/20">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-yellow-400" /> Top Performer</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-bold text-lg">{topPerformer.rep_name}</p>
                  <p className="text-3xl font-bold text-yellow-400">{topPerformer.score}</p>
                  <p className="text-xs text-muted-foreground">{topPerformer.sold_count} sold · {topPerformer.demos_count} demos</p>
                </CardContent>
              </Card>
            )}

            {mostDemos && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Most Demos</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-medium">{mostDemos.rep_name}</p>
                  <p className="text-2xl font-bold">{mostDemos.demos_count} demos</p>
                </CardContent>
              </Card>
            )}

            {bestFollowUp && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-green-400" /> Best Follow-Up</CardTitle></CardHeader>
                <CardContent>
                  <p className="font-medium">{bestFollowUp.rep_name}</p>
                  <p className="text-2xl font-bold">{bestFollowUp.followup_rate}%</p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Team Goal Progress</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span>Monthly Target</span><span className="font-medium">23 / 30 sold</span></div>
                  <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: '77%' }} />
                  </div>
                  <p className="text-xs text-muted-foreground">77% — 7 more to hit goal</p>
                </div>
              </CardContent>
            </Card>

            {atRisk.length > 0 && (
              <Card className="border-red-500/20">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /> At Risk Reps</CardTitle></CardHeader>
                <CardContent>
                  {atRisk.map(s => (
                    <div key={s.id} className="flex items-center justify-between py-2">
                      <span className="text-sm">{s.rep_name}</span>
                      <span className="text-sm font-medium text-red-400">{s.score}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Tabs>
    </div>
  );
}
